import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../../components/trading/Trading.module.css';
import {
  OptionContractLookupRow,
  OrderPreview,
  OrderSide,
  OrderTicketPrefill,
  OrderType,
  PlaceOrderRequest,
  TickL1,
} from '../../../types/trading';
import { TickerDropdown } from '../../../components/common/TickerDropdown';
import { InfoTooltip } from '../../../components/common/InfoTooltip';
import {
  buildManualOrderStorageKey,
  readSessionStorageJson,
  removeSessionStorageKey,
  writeSessionStorageJson,
} from '../persistence/orderTicketPersistence';

interface OrderTicketProps {
  selectedSymbol: string | null;
  ticksBySymbol: Record<string, TickL1>;
  onSubscribeSymbol: (symbol: string) => void;
  onUnsubscribeSymbol: (symbol: string) => void;
  onPreview: (request: PlaceOrderRequest) => Promise<OrderPreview>;
  onPlaceOrder: (request: PlaceOrderRequest) => Promise<unknown>;
  isSimulationEnabled?: boolean | null;
  onFetchMarketOpen?: (symbol: string) => Promise<boolean>;
  onLoadOptionContractsByType: (
    underlying: string,
    optionType: OptionSide,
  ) => Promise<OptionContractLookupRow[]>;
  onLoadOptionContractsByStrike: (
    underlying: string,
    optionType: OptionSide,
    strike: number,
  ) => Promise<OptionContractLookupRow[]>;
  onLoadOptionContractsByExpiration: (
    underlying: string,
    optionType: OptionSide,
    expiration: string,
  ) => Promise<OptionContractLookupRow[]>;
  disabled?: boolean;
  closeRequest?: OrderTicketPrefill | null;
  persistenceScope?: string;
  onOrderPlaced?: () => void;
}

type InstrumentType = 'stock' | 'option';
type OptionSide = 'call' | 'put';
type PremiumDirection = 'credit' | 'debit';
type OptionOrderType = Exclude<OrderType, 'limit'>;
type ContractFetchMode = 'type' | 'strike' | 'expiration';

interface OptionLegDraft {
  id: string;
  side: OrderSide;
  optionSide: OptionSide;
  expiration: string;
  strike: number | '';
  quantity: number;
}

interface OptionLegResolved extends OptionLegDraft {
  symbol: string;
  tick?: TickL1;
  isComplete: boolean;
}

interface OptionLegPreviewResult {
  legId: string;
  request: PlaceOrderRequest;
  preview: OrderPreview;
}

interface OptionConfirmLeg {
  leg: OptionLegResolved;
  request: PlaceOrderRequest;
  preview: OrderPreview | null;
}

type ConfirmSnapshot =
  | {
      mode: 'stock';
      request: PlaceOrderRequest;
      preview: OrderPreview | null;
    }
  | {
      mode: 'option';
      orderType: OptionOrderType;
      legs: OptionConfirmLeg[];
    };
const MAX_OPTION_LEGS = 6;
const MIN_OPTION_LEGS = 1;
const MARKET_HOURS_BLOCKED_MESSAGE = 'cannot place a manual trade outside market hours';
const CRYPTO_PAIR_SYMBOL_PATTERN = /^[A-Z0-9]+\/[A-Z0-9]+$/;

const formatQuote = (value?: number | null): string => (value == null ? '--' : value.toFixed(2));
const formatCurrency = (value: number): string => {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const premiumClassName = (direction: PremiumDirection | null): string => {
  if (direction === 'credit') return styles.premiumCredit;
  if (direction === 'debit') return styles.premiumDebit;
  return '';
};

const STRIKE_EPSILON = 0.000001;

const strikesEqual = (left: number, right: number): boolean => (
  Math.abs(left - right) < STRIKE_EPSILON
);

const uniqueSortedExpirations = (contracts: OptionContractLookupRow[]): string[] => (
  Array.from(new Set(contracts.map((contract) => contract.expiration))).sort()
);

const uniqueSortedStrikes = (contracts: OptionContractLookupRow[]): number[] => {
  const sorted = contracts
    .map((contract) => contract.strike)
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);

  const unique: number[] = [];
  sorted.forEach((value) => {
    const last = unique[unique.length - 1];
    if (last === undefined || !strikesEqual(last, value)) {
      unique.push(value);
    }
  });
  return unique;
};

const resolveContractFetchMode = (leg: OptionLegDraft): ContractFetchMode => {
  if (leg.strike !== '' && Number.isFinite(leg.strike)) return 'strike';
  if (leg.expiration) return 'expiration';
  return 'type';
};

const resolveContractFetchModeForLeg = (
  leg: OptionLegDraft,
  preferredMode: ContractFetchMode | null | undefined,
): ContractFetchMode => {
  if (preferredMode === 'expiration' && leg.expiration) return 'expiration';
  if (preferredMode === 'strike' && leg.strike !== '' && Number.isFinite(leg.strike)) return 'strike';
  return resolveContractFetchMode(leg);
};

const pruneRecordByKeys = <T,>(record: Record<string, T>, keep: Set<string>): Record<string, T> => {
  const pruned: Record<string, T> = {};
  Object.keys(record).forEach((key) => {
    if (keep.has(key)) {
      pruned[key] = record[key];
    }
  });
  return pruned;
};

const buildClientOrderId = () => `coid-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

const parseOptionSymbol = (
  symbol: string,
): { underlying: string; expiration: string; optionSide: OptionSide; strike: number } | null => {
  const normalized = symbol.trim().toUpperCase();
  const match = normalized.match(/^(?:O:)?([A-Z]+)(\d{6})([CP])(\d+)$/);
  if (!match) return null;

  const [, ticker, yymmdd, cpFlag, strikeCode] = match;
  const yy = yymmdd.slice(0, 2);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);

  return {
    underlying: ticker,
    expiration: `20${yy}-${mm}-${dd}`,
    optionSide: cpFlag === 'C' ? 'call' : 'put',
    strike: parseInt(strikeCode, 10) / 1000,
  };
};

const formatOptionStrike = (strike: number): string => {
  return strike.toFixed(3).replace(/\.?0+$/, '');
};

const formatOptionResolvedSymbol = (symbol: string): string => {
  const parsed = parseOptionSymbol(symbol);
  if (!parsed) return symbol;
  const sideLabel = parsed.optionSide === 'call' ? 'Call' : 'Put';
  return `${parsed.underlying} ${parsed.expiration} ${sideLabel} ${formatOptionStrike(parsed.strike)}`;
};

const isCryptoPairSymbol = (symbol: string): boolean => {
  const normalized = symbol.trim().toUpperCase();
  return CRYPTO_PAIR_SYMBOL_PATTERN.test(normalized);
};

const sideAwareQuoteFallback = (
  side: OrderSide,
  orderType: OrderType,
  tick?: TickL1,
): number | null => {
  if (!tick) return null;

  if (orderType === 'best_fill') {
    return side === 'buy' ? tick.bid : tick.ask;
  }

  return side === 'buy' ? tick.ask : tick.bid;
};

const resolveSignedNotional = ({
  estimatedFillPrice,
  side,
  quantity,
  orderType,
  tick,
}: {
  estimatedFillPrice: number | null;
  side: OrderSide;
  quantity: number;
  orderType: OrderType;
  tick?: TickL1;
}): number | null => {
  const resolvedPrice =
    typeof estimatedFillPrice === 'number' && Number.isFinite(estimatedFillPrice)
      ? estimatedFillPrice
      : sideAwareQuoteFallback(side, orderType, tick);

  if (resolvedPrice == null || !Number.isFinite(quantity) || quantity <= 0) {
    return null;
  }

  return (side === 'sell' ? -1 : 1) * resolvedPrice * quantity;
};

const formatSignedPremium = (
  signedNotional: number | null,
  includeDirectionLabel = true,
): { premiumText: string; direction: PremiumDirection | null } => {
  if (signedNotional == null || !Number.isFinite(signedNotional)) {
    return { premiumText: '--', direction: null };
  }

  const direction: PremiumDirection = signedNotional < 0 ? 'credit' : 'debit';
  const formattedPremium = formatCurrency(Math.abs(signedNotional));
  return {
    premiumText: includeDirectionLabel
      ? `${formattedPremium} ${direction === 'credit' ? 'Credit' : 'Debit'}`
      : formattedPremium,
    direction,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const sanitizeOrderSide = (value: unknown, fallback: OrderSide): OrderSide =>
  value === 'buy' || value === 'sell' ? value : fallback;

const sanitizeInstrumentType = (value: unknown, fallback: InstrumentType): InstrumentType =>
  value === 'stock' || value === 'option' ? value : fallback;

const sanitizeOptionSide = (value: unknown, fallback: OptionSide): OptionSide =>
  value === 'call' || value === 'put' ? value : fallback;

const sanitizeOrderType = (value: unknown, fallback: OrderType): OrderType =>
  value === 'market' || value === 'limit' || value === 'best_fill' ? value : fallback;

const sanitizeStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const sanitizeFiniteNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const sanitizePositiveNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

const sanitizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
};

const sanitizeTick = (value: unknown): TickL1 | undefined => {
  if (!isRecord(value)) return undefined;
  if (typeof value.symbol !== 'string') return undefined;
  if (typeof value.ts !== 'string') return undefined;

  const sanitizeQuote = (quote: unknown): number | null => {
    if (quote == null) return null;
    if (typeof quote === 'number' && Number.isFinite(quote)) return quote;
    return null;
  };

  return {
    symbol: value.symbol,
    bid: sanitizeQuote(value.bid),
    ask: sanitizeQuote(value.ask),
    last: sanitizeQuote(value.last),
    ts: value.ts,
  };
};

const sanitizeOrderPreview = (value: unknown): OrderPreview | null => {
  if (!isRecord(value)) return null;
  if (typeof value.accepted !== 'boolean') return null;
  if (typeof value.estimated_notional !== 'number' || !Number.isFinite(value.estimated_notional)) return null;
  if (value.estimated_fill_price != null && (typeof value.estimated_fill_price !== 'number' || !Number.isFinite(value.estimated_fill_price))) {
    return null;
  }

  const warnings = sanitizeStringArray(value.warnings);
  const risk = isRecord(value.risk) ? value.risk : null;
  if (!risk || typeof risk.passed !== 'boolean') return null;

  return {
    accepted: value.accepted,
    estimated_notional: value.estimated_notional,
    estimated_fill_price: value.estimated_fill_price as number | null,
    warnings,
    risk: {
      passed: risk.passed,
      reasons: sanitizeStringArray(risk.reasons),
    },
  };
};

const sanitizePlaceOrderRequest = (value: unknown): PlaceOrderRequest | null => {
  if (!isRecord(value)) return null;

  if (typeof value.symbol !== 'string' || !value.symbol.trim()) return null;
  if (typeof value.quantity !== 'number' || !Number.isFinite(value.quantity) || value.quantity <= 0) return null;

  const side = sanitizeOrderSide(value.side, 'buy');
  const orderType = sanitizeOrderType(value.order_type, 'market');
  const clientOrderId = typeof value.client_order_id === 'string' && value.client_order_id
    ? value.client_order_id
    : buildClientOrderId();

  const request: PlaceOrderRequest = {
    client_order_id: clientOrderId,
    symbol: value.symbol,
    side,
    quantity: value.quantity,
    order_type: orderType,
  };

  if (typeof value.limit_price === 'number' && Number.isFinite(value.limit_price)) {
    request.limit_price = value.limit_price;
  }

  return request;
};

const sanitizeOptionLegDraft = (value: unknown, fallbackId: string): OptionLegDraft | null => {
  if (!isRecord(value)) return null;

  const id = typeof value.id === 'string' && value.id.trim() ? value.id : fallbackId;
  const side = sanitizeOrderSide(value.side, 'buy');
  const optionSide = sanitizeOptionSide(value.optionSide, 'call');
  const expiration = typeof value.expiration === 'string' ? value.expiration : '';

  const strike = typeof value.strike === 'number' && Number.isFinite(value.strike) && value.strike > 0
    ? value.strike
    : '';

  const quantity = sanitizePositiveNumber(value.quantity, 1);

  return {
    id,
    side,
    optionSide,
    expiration,
    strike,
    quantity,
  };
};

const sanitizeOptionLegResolved = (value: unknown, fallbackId: string): OptionLegResolved | null => {
  if (!isRecord(value)) return null;
  const draft = sanitizeOptionLegDraft(value, fallbackId);
  if (!draft) return null;

  const symbol = typeof value.symbol === 'string' ? value.symbol : '';
  const tick = sanitizeTick(value.tick);
  const isComplete = typeof value.isComplete === 'boolean'
    ? value.isComplete
    : Boolean(
      symbol &&
      draft.expiration &&
      draft.strike !== '' &&
      Number.isFinite(draft.quantity) &&
      draft.quantity > 0
    );

  return {
    ...draft,
    symbol,
    tick,
    isComplete,
  };
};

const sanitizeOptionLegPreviewResult = (value: unknown): OptionLegPreviewResult | null => {
  if (!isRecord(value)) return null;
  if (typeof value.legId !== 'string' || !value.legId) return null;

  const request = sanitizePlaceOrderRequest(value.request);
  const preview = sanitizeOrderPreview(value.preview);
  if (!request || !preview) return null;

  return {
    legId: value.legId,
    request,
    preview,
  };
};

const sanitizeOptionConfirmLeg = (value: unknown, fallbackLegId: string): OptionConfirmLeg | null => {
  if (!isRecord(value)) return null;

  const leg = sanitizeOptionLegResolved(value.leg, fallbackLegId);
  const request = sanitizePlaceOrderRequest(value.request);
  if (!leg || !request) return null;

  const hasPreview = value.preview !== null && value.preview !== undefined;
  const preview = hasPreview ? sanitizeOrderPreview(value.preview) : null;
  if (hasPreview && !preview) return null;

  return {
    leg,
    request,
    preview,
  };
};

const sanitizeConfirmSnapshot = (value: unknown): ConfirmSnapshot | null => {
  if (!isRecord(value)) return null;

  if (value.mode === 'stock') {
    const request = sanitizePlaceOrderRequest(value.request);
    if (!request) return null;

    const hasPreview = value.preview !== null && value.preview !== undefined;
    const preview = hasPreview ? sanitizeOrderPreview(value.preview) : null;
    if (hasPreview && !preview) return null;

    return {
      mode: 'stock',
      request,
      preview,
    };
  }

  if (value.mode === 'option') {
    const orderType = value.orderType === 'market' || value.orderType === 'best_fill'
      ? value.orderType
      : null;

    if (!orderType || !Array.isArray(value.legs)) return null;

    const legs = value.legs
      .map((entry, index) => sanitizeOptionConfirmLeg(entry, `leg-${index + 1}`))
      .filter((entry): entry is OptionConfirmLeg => Boolean(entry));

    if (legs.length !== value.legs.length) return null;

    return {
      mode: 'option',
      orderType,
      legs,
    };
  }

  return null;
};

interface OrderTicketPersistedState {
  instrumentType: InstrumentType;
  underlying: string;
  stockSide: OrderSide;
  stockQuantity: number;
  optionLegs: OptionLegDraft[];
  orderType: OrderType;
  limitPrice: number;
  stockPreview: OrderPreview | null;
  optionPreviewLegs: OptionLegPreviewResult[] | null;
  submitting: boolean;
  previewing: boolean;
  error: string | null;
  isConfirmOpen: boolean;
  confirmSnapshot: ConfirmSnapshot | null;
  isSuccessOpen: boolean;
  successMessage: string | null;
}

const buildDefaultOrderTicketState = (
  createOptionLeg: (overrides?: Partial<Omit<OptionLegDraft, 'id'>>) => OptionLegDraft,
): OrderTicketPersistedState => ({
  instrumentType: 'option',
  underlying: 'AAPL',
  stockSide: 'buy',
  stockQuantity: 1,
  optionLegs: [createOptionLeg()],
  orderType: 'market',
  limitPrice: 0,
  stockPreview: null,
  optionPreviewLegs: null,
  submitting: false,
  previewing: false,
  error: null,
  isConfirmOpen: false,
  confirmSnapshot: null,
  isSuccessOpen: false,
  successMessage: null,
});

const sanitizeOrderTicketState = (
  value: unknown,
  createOptionLeg: (overrides?: Partial<Omit<OptionLegDraft, 'id'>>) => OptionLegDraft,
): OrderTicketPersistedState | null => {
  if (!isRecord(value)) return null;

  const fallback = buildDefaultOrderTicketState(createOptionLeg);
  const optionLegsSource = Array.isArray(value.optionLegs) ? value.optionLegs : [];
  const optionLegs = optionLegsSource
    .map((leg, index) => sanitizeOptionLegDraft(leg, `leg-${index + 1}`))
    .filter((leg): leg is OptionLegDraft => Boolean(leg));
  const normalizedOptionLegs = optionLegs.length > 0 ? optionLegs : fallback.optionLegs;

  const stockPreview = value.stockPreview == null ? null : sanitizeOrderPreview(value.stockPreview);
  const optionPreviewLegsSource = Array.isArray(value.optionPreviewLegs) ? value.optionPreviewLegs : null;
  const optionPreviewLegs = optionPreviewLegsSource
    ? optionPreviewLegsSource
      .map((entry) => sanitizeOptionLegPreviewResult(entry))
      .filter((entry): entry is OptionLegPreviewResult => Boolean(entry))
    : null;
  const confirmSnapshot = value.confirmSnapshot == null ? null : sanitizeConfirmSnapshot(value.confirmSnapshot);
  const successMessage = sanitizeStringOrNull(value.successMessage);

  return {
    instrumentType: sanitizeInstrumentType(value.instrumentType, fallback.instrumentType),
    underlying: typeof value.underlying === 'string' ? value.underlying : fallback.underlying,
    stockSide: sanitizeOrderSide(value.stockSide, fallback.stockSide),
    stockQuantity: sanitizePositiveNumber(value.stockQuantity, fallback.stockQuantity),
    optionLegs: normalizedOptionLegs,
    orderType: sanitizeOrderType(value.orderType, fallback.orderType),
    limitPrice: sanitizeFiniteNumber(value.limitPrice, fallback.limitPrice),
    stockPreview,
    optionPreviewLegs,
    submitting: value.submitting === true,
    previewing: value.previewing === true,
    error: sanitizeStringOrNull(value.error),
    isConfirmOpen: value.isConfirmOpen === true && Boolean(confirmSnapshot),
    confirmSnapshot,
    isSuccessOpen: value.isSuccessOpen === true && Boolean(successMessage),
    successMessage,
  };
};

export const OrderTicket: React.FC<OrderTicketProps> = ({
  selectedSymbol,
  ticksBySymbol,
  onSubscribeSymbol,
  onUnsubscribeSymbol,
  onPreview,
  onPlaceOrder,
  isSimulationEnabled = null,
  onFetchMarketOpen,
  onLoadOptionContractsByType,
  onLoadOptionContractsByStrike,
  onLoadOptionContractsByExpiration,
  disabled = false,
  closeRequest,
  persistenceScope,
  onOrderPlaced,
}) => {
  const optionLegCounterRef = useRef(1);
  const subscribedSymbolsRef = useRef<Set<string>>(new Set());
  const optionContractsRequestCounterRef = useRef<Record<string, number>>({});
  const optionContractsQueryKeyRef = useRef<Record<string, string>>({});
  const optionContractsPreferredModeByLegRef = useRef<Record<string, ContractFetchMode | null>>({});
  const [hasHydratedPersistence, setHasHydratedPersistence] = useState(false);

  const createOptionLeg = useCallback((overrides: Partial<Omit<OptionLegDraft, 'id'>> = {}): OptionLegDraft => {
    const next: OptionLegDraft = {
      id: `leg-${optionLegCounterRef.current++}`,
      side: 'buy',
      optionSide: 'call',
      expiration: '',
      strike: '',
      quantity: 1,
      ...overrides,
    };
    return next;
  }, []);

  const [instrumentType, setInstrumentType] = useState<InstrumentType>('option');
  const [underlying, setUnderlying] = useState('AAPL');

  const [stockSide, setStockSide] = useState<OrderSide>('buy');
  const [stockQuantity, setStockQuantity] = useState<number>(1);

  const [optionLegs, setOptionLegs] = useState<OptionLegDraft[]>(() => [createOptionLeg()]);

  const [orderType, setOrderType] = useState<OrderType>('market');
  const [limitPrice, setLimitPrice] = useState<number>(0);

  const [stockPreview, setStockPreview] = useState<OrderPreview | null>(null);
  const [optionPreviewLegs, setOptionPreviewLegs] = useState<OptionLegPreviewResult[] | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmSnapshot, setConfirmSnapshot] = useState<ConfirmSnapshot | null>(null);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isMarketHoursBlockedModalOpen, setIsMarketHoursBlockedModalOpen] = useState(false);
  const [optionContractsByLeg, setOptionContractsByLeg] = useState<Record<string, OptionContractLookupRow[]>>({});
  const [optionContractsLoadingByLeg, setOptionContractsLoadingByLeg] = useState<Record<string, boolean>>({});
  const [optionContractsErrorByLeg, setOptionContractsErrorByLeg] = useState<Record<string, string | null>>({});

  const manualStorageKey = useMemo(() => (
    persistenceScope ? buildManualOrderStorageKey(persistenceScope) : null
  ), [persistenceScope]);

  const syncOptionLegCounter = useCallback((legs: OptionLegDraft[]) => {
    let maxLegNumber = 0;
    legs.forEach((leg) => {
      const match = leg.id.match(/^leg-(\d+)$/);
      if (!match) return;
      const candidate = Number(match[1]);
      if (Number.isFinite(candidate)) {
        maxLegNumber = Math.max(maxLegNumber, candidate);
      }
    });

    optionLegCounterRef.current = Math.max(maxLegNumber + 1, 1);
  }, []);

  const applyOrderTicketState = useCallback((next: OrderTicketPersistedState) => {
    setInstrumentType(next.instrumentType);
    setUnderlying(next.underlying);
    setStockSide(next.stockSide);
    setStockQuantity(next.stockQuantity);
    setOptionLegs(next.optionLegs);
    setOrderType(next.orderType);
    setLimitPrice(next.limitPrice);
    setStockPreview(next.stockPreview);
    setOptionPreviewLegs(next.optionPreviewLegs);
    setSubmitting(next.submitting);
    setPreviewing(next.previewing);
    setError(next.error);
    setConfirmSnapshot(next.confirmSnapshot);
    setIsConfirmOpen(next.isConfirmOpen);
    setSuccessMessage(next.successMessage);
    setIsSuccessOpen(next.isSuccessOpen);
    syncOptionLegCounter(next.optionLegs);
  }, [syncOptionLegCounter]);

  const resetPersistedState = useCallback(() => {
    const defaults = buildDefaultOrderTicketState(createOptionLeg);
    if (manualStorageKey) {
      removeSessionStorageKey(manualStorageKey);
    }
    applyOrderTicketState(defaults);
    setHasHydratedPersistence(true);
  }, [applyOrderTicketState, createOptionLeg, manualStorageKey]);

  const upperUnderlying = underlying.trim().toUpperCase();
  const stockSymbol = upperUnderlying;

  useEffect(() => {
    const activeLegIds = new Set(optionLegs.map((leg) => leg.id));
    setOptionContractsByLeg((prev) => pruneRecordByKeys(prev, activeLegIds));
    setOptionContractsLoadingByLeg((prev) => pruneRecordByKeys(prev, activeLegIds));
    setOptionContractsErrorByLeg((prev) => pruneRecordByKeys(prev, activeLegIds));

    Object.keys(optionContractsRequestCounterRef.current).forEach((legId) => {
      if (!activeLegIds.has(legId)) {
        delete optionContractsRequestCounterRef.current[legId];
        delete optionContractsQueryKeyRef.current[legId];
        delete optionContractsPreferredModeByLegRef.current[legId];
      }
    });
  }, [optionLegs]);

  useEffect(() => {
    if (instrumentType !== 'option' || !upperUnderlying) {
      setOptionContractsByLeg({});
      setOptionContractsLoadingByLeg({});
      setOptionContractsErrorByLeg({});
      optionContractsRequestCounterRef.current = {};
      optionContractsQueryKeyRef.current = {};
      optionContractsPreferredModeByLegRef.current = {};
      return;
    }

    let canceled = false;
    optionLegs.forEach((leg) => {
      const preferredMode = optionContractsPreferredModeByLegRef.current[leg.id];
      const mode = resolveContractFetchModeForLeg(leg, preferredMode);
      const strikePart = leg.strike === '' ? '' : Number(leg.strike).toString();
      const queryKey = [
        upperUnderlying,
        leg.optionSide,
        mode,
        leg.expiration,
        strikePart,
      ].join('|');

      if (optionContractsQueryKeyRef.current[leg.id] === queryKey) {
        return;
      }

      optionContractsQueryKeyRef.current[leg.id] = queryKey;
      const requestId = (optionContractsRequestCounterRef.current[leg.id] ?? 0) + 1;
      optionContractsRequestCounterRef.current[leg.id] = requestId;

      setOptionContractsLoadingByLeg((prev) => ({ ...prev, [leg.id]: true }));
      setOptionContractsErrorByLeg((prev) => ({ ...prev, [leg.id]: null }));
      setOptionContractsByLeg((prev) => ({ ...prev, [leg.id]: [] }));

      const run = async () => {
        try {
          let contracts: OptionContractLookupRow[] = [];
          if (mode === 'strike' && leg.strike !== '') {
            contracts = await onLoadOptionContractsByStrike(
              upperUnderlying,
              leg.optionSide,
              Number(leg.strike),
            );
          } else if (mode === 'expiration' && leg.expiration) {
            contracts = await onLoadOptionContractsByExpiration(
              upperUnderlying,
              leg.optionSide,
              leg.expiration,
            );
          } else {
            contracts = await onLoadOptionContractsByType(
              upperUnderlying,
              leg.optionSide,
            );
          }

          if (canceled || optionContractsRequestCounterRef.current[leg.id] !== requestId) return;
          setOptionContractsByLeg((prev) => ({ ...prev, [leg.id]: contracts }));
        } catch (err: any) {
          if (canceled || optionContractsRequestCounterRef.current[leg.id] !== requestId) return;
          const message = err?.response?.data?.error || err?.message || 'Failed to load option contracts';
          setOptionContractsErrorByLeg((prev) => ({ ...prev, [leg.id]: message }));
          setOptionContractsByLeg((prev) => ({ ...prev, [leg.id]: [] }));
        } finally {
          if (canceled || optionContractsRequestCounterRef.current[leg.id] !== requestId) return;
          setOptionContractsLoadingByLeg((prev) => ({ ...prev, [leg.id]: false }));
        }
      };

      void run();
    });

    return () => {
      canceled = true;
    };
  }, [
    instrumentType,
    upperUnderlying,
    optionLegs,
    onLoadOptionContractsByType,
    onLoadOptionContractsByStrike,
    onLoadOptionContractsByExpiration,
  ]);

  const availableExpirationsByLeg = useMemo(() => {
    const next: Record<string, string[]> = {};
    optionLegs.forEach((leg) => {
      next[leg.id] = uniqueSortedExpirations(optionContractsByLeg[leg.id] ?? []);
    });
    return next;
  }, [optionLegs, optionContractsByLeg]);

  const availableStrikesByLeg = useMemo(() => {
    const next: Record<string, number[]> = {};
    optionLegs.forEach((leg) => {
      next[leg.id] = uniqueSortedStrikes(optionContractsByLeg[leg.id] ?? []);
    });
    return next;
  }, [optionLegs, optionContractsByLeg]);

  const optionLegsResolved = useMemo<OptionLegResolved[]>(() => {
    return optionLegs.map((leg) => {
      const contracts = optionContractsByLeg[leg.id] ?? [];
      const selectedStrike = leg.strike === '' ? null : Number(leg.strike);
      const matchingContract = (
        selectedStrike != null &&
        leg.expiration
      )
        ? contracts.find(
          (contract) => (
            contract.expiration === leg.expiration &&
            contract.option_type === leg.optionSide &&
            strikesEqual(contract.strike, selectedStrike)
          ),
        )
        : undefined;

      const symbol = matchingContract?.symbol ?? '';
      const isComplete = Boolean(
        upperUnderlying &&
          leg.expiration &&
          leg.strike !== '' &&
          symbol &&
          Number.isFinite(leg.quantity) &&
          leg.quantity > 0,
      );
      const tick = symbol ? ticksBySymbol[symbol] : undefined;
      return {
        ...leg,
        symbol,
        tick,
        isComplete,
      };
    });
  }, [optionLegs, optionContractsByLeg, upperUnderlying, ticksBySymbol]);

  const optionOrderType: OptionOrderType = orderType === 'best_fill' ? 'best_fill' : 'market';

  const optionPreviewMap = useMemo(() => {
    const map = new Map<string, OptionLegPreviewResult>();
    optionPreviewLegs?.forEach((entry) => {
      map.set(entry.legId, entry);
    });
    return map;
  }, [optionPreviewLegs]);

  const desiredQuoteSymbols = useMemo(() => {
    const symbols = new Set<string>();

    if (instrumentType === 'stock') {
      if (stockSymbol) symbols.add(stockSymbol);
    } else {
      optionLegsResolved.forEach((leg) => {
        if (leg.symbol) symbols.add(leg.symbol);
      });
    }

    return Array.from(symbols).sort();
  }, [instrumentType, stockSymbol, optionLegsResolved]);

  const desiredQuoteKey = desiredQuoteSymbols.join('|');

  useEffect(() => {
    const current = subscribedSymbolsRef.current;
    const next = new Set(desiredQuoteSymbols);

    current.forEach((symbol) => {
      if (!next.has(symbol)) {
        onUnsubscribeSymbol(symbol);
      }
    });

    next.forEach((symbol) => {
      if (!current.has(symbol)) {
        onSubscribeSymbol(symbol);
      }
    });

    subscribedSymbolsRef.current = next;
  }, [desiredQuoteKey, desiredQuoteSymbols, onSubscribeSymbol, onUnsubscribeSymbol]);

  useEffect(() => {
    return () => {
      subscribedSymbolsRef.current.forEach((symbol) => onUnsubscribeSymbol(symbol));
      subscribedSymbolsRef.current.clear();
    };
  }, [onUnsubscribeSymbol]);

  useEffect(() => {
    if (instrumentType === 'option' && orderType === 'limit') {
      setOrderType('market');
    }
  }, [instrumentType, orderType]);

  useEffect(() => {
    setHasHydratedPersistence(false);

    const defaults = buildDefaultOrderTicketState(createOptionLeg);
    if (!manualStorageKey) {
      applyOrderTicketState(defaults);
      setHasHydratedPersistence(true);
      return;
    }

    const saved = readSessionStorageJson<unknown>(manualStorageKey);
    const restored = sanitizeOrderTicketState(saved, createOptionLeg);
    applyOrderTicketState(restored ?? defaults);
    setHasHydratedPersistence(true);
  }, [manualStorageKey, applyOrderTicketState, createOptionLeg]);

  useEffect(() => {
    if (!manualStorageKey || !hasHydratedPersistence) return;

    const snapshot: OrderTicketPersistedState = {
      instrumentType,
      underlying,
      stockSide,
      stockQuantity,
      optionLegs,
      orderType,
      limitPrice,
      stockPreview,
      optionPreviewLegs,
      submitting,
      previewing,
      error,
      isConfirmOpen,
      confirmSnapshot,
      isSuccessOpen,
      successMessage,
    };

    writeSessionStorageJson(manualStorageKey, snapshot);
  }, [
    manualStorageKey,
    hasHydratedPersistence,
    instrumentType,
    underlying,
    stockSide,
    stockQuantity,
    optionLegs,
    orderType,
    limitPrice,
    stockPreview,
    optionPreviewLegs,
    submitting,
    previewing,
    error,
    isConfirmOpen,
    confirmSnapshot,
    isSuccessOpen,
    successMessage,
  ]);

  useEffect(() => {
    if (!selectedSymbol) return;
    const normalized = selectedSymbol.trim().toUpperCase();
    if (!normalized) return;

    const parsed = parseOptionSymbol(normalized);

    setError(null);
    setStockPreview(null);
    setOptionPreviewLegs(null);

    if (parsed) {
      setInstrumentType('option');
      setUnderlying(parsed.underlying);
      setOptionLegs([
        createOptionLeg({
          optionSide: parsed.optionSide,
          expiration: parsed.expiration,
          strike: parsed.strike,
        }),
      ]);
      return;
    }

    setInstrumentType('stock');
    setUnderlying(normalized);
  }, [selectedSymbol, createOptionLeg]);

  useEffect(() => {
    if (!closeRequest) return;

    setError(null);
    setStockPreview(null);
    setOptionPreviewLegs(null);
    const incomingOrderType = closeRequest.order_type;

    if (closeRequest.instrument_type === 'option') {
      const explicitUnderlying = closeRequest.underlying.trim().toUpperCase();
      const normalizedLegs = closeRequest.option_legs.map((leg) => {
        const parsed = leg.symbol ? parseOptionSymbol(leg.symbol) : null;
        const strikeValue = Number.isFinite(leg.strike) && leg.strike > 0
          ? leg.strike
          : (parsed?.strike ?? '');

        return createOptionLeg({
          side: leg.side,
          optionSide: leg.option_side ?? parsed?.optionSide ?? 'call',
          expiration: leg.expiration || parsed?.expiration || '',
          strike: strikeValue,
          quantity: Number.isFinite(leg.quantity) && leg.quantity > 0 ? leg.quantity : 1,
        });
      });

      const parsedUnderlyingFromLeg = closeRequest.option_legs
        .map((leg) => (leg.symbol ? parseOptionSymbol(leg.symbol) : null))
        .find((parsed) => Boolean(parsed))
        ?.underlying;
      const resolvedUnderlying = explicitUnderlying || parsedUnderlyingFromLeg || 'AAPL';

      setInstrumentType('option');
      setUnderlying(resolvedUnderlying);
      setOptionLegs(normalizedLegs.length > 0 ? normalizedLegs : [createOptionLeg()]);
      setOrderType(incomingOrderType === 'best_fill' ? 'best_fill' : 'market');
      return;
    }

    const normalizedSymbol = closeRequest.symbol.trim().toUpperCase();
    const parsed = parseOptionSymbol(normalizedSymbol);

    if (parsed) {
      setInstrumentType('option');
      setUnderlying(parsed.underlying);
      setOptionLegs([
        createOptionLeg({
          side: closeRequest.side,
          optionSide: parsed.optionSide,
          expiration: parsed.expiration,
          strike: parsed.strike,
          quantity: closeRequest.quantity,
        }),
      ]);
      setOrderType(incomingOrderType === 'best_fill' ? 'best_fill' : 'market');
      return;
    }

    setInstrumentType('stock');
    setUnderlying(normalizedSymbol);
    setStockSide(closeRequest.side);
    setStockQuantity(closeRequest.quantity);
    setOrderType(incomingOrderType ?? 'market');
    if (incomingOrderType === 'limit' && typeof closeRequest.limit_price === 'number' && closeRequest.limit_price > 0) {
      setLimitPrice(closeRequest.limit_price);
    }
  }, [closeRequest, createOptionLeg]);

  const clearPreviewState = () => {
    setStockPreview(null);
    setOptionPreviewLegs(null);
  };

  const updateOptionLeg = (legId: string, updater: (leg: OptionLegDraft) => OptionLegDraft) => {
    setOptionLegs((prev) => prev.map((leg) => (leg.id === legId ? updater(leg) : leg)));
    clearPreviewState();
  };

  const addOptionLeg = () => {
    setOptionLegs((prev) => {
      if (prev.length >= MAX_OPTION_LEGS) return prev;
      return [...prev, createOptionLeg()];
    });
    clearPreviewState();
  };

  const removeOptionLeg = (legId: string) => {
    setOptionLegs((prev) => {
      if (prev.length <= MIN_OPTION_LEGS) return prev;
      return prev.filter((leg) => leg.id !== legId);
    });
    clearPreviewState();
  };

  const stockQuoteTick = stockSymbol ? ticksBySymbol[stockSymbol] : undefined;

  const stockPreviewPremium = useMemo(() => {
    if (!stockPreview) return null;
    const signedNotional = resolveSignedNotional({
      estimatedFillPrice: stockPreview.estimated_fill_price,
      side: stockSide,
      quantity: stockQuantity,
      orderType,
      tick: stockQuoteTick,
    });
    return formatSignedPremium(signedNotional);
  }, [stockPreview, stockSide, stockQuantity, orderType, stockQuoteTick]);

  const optionCombinedQuote = useMemo(() => {
    if (instrumentType !== 'option') return null;
    if (optionLegsResolved.length === 0) return null;

    let combinedBid = 0;
    let combinedAsk = 0;

    for (const leg of optionLegsResolved) {
      if (!leg.isComplete) return null;

      const sign = leg.side === 'buy' ? 1 : -1;
      const legBidQuote = leg.side === 'buy' ? leg.tick?.bid : leg.tick?.ask;
      const legAskQuote = leg.side === 'buy' ? leg.tick?.ask : leg.tick?.bid;

      if (
        typeof legBidQuote !== 'number' ||
        !Number.isFinite(legBidQuote) ||
        typeof legAskQuote !== 'number' ||
        !Number.isFinite(legAskQuote)
      ) {
        return null;
      }

      combinedBid += sign * legBidQuote * leg.quantity;
      combinedAsk += sign * legAskQuote * leg.quantity;
    }

    return {
      bid: combinedBid,
      ask: combinedAsk,
      mid: (combinedBid + combinedAsk) / 2,
      bidPremium: formatSignedPremium(combinedBid, false),
      askPremium: formatSignedPremium(combinedAsk, false),
      midPremium: formatSignedPremium((combinedBid + combinedAsk) / 2, false),
    };
  }, [instrumentType, optionLegsResolved]);

  const optionPreviewMeta = useMemo(() => {
    if (!optionPreviewLegs || optionPreviewLegs.length === 0) return null;

    const warnings: string[] = [];
    const riskReasons: string[] = [];

    let combinedSignedNotional = 0;

    for (const leg of optionLegsResolved) {
      const legPreview = optionPreviewMap.get(leg.id);
      if (!legPreview) return null;

      warnings.push(...legPreview.preview.warnings);
      if (!legPreview.preview.risk.passed) {
        riskReasons.push(...legPreview.preview.risk.reasons);
      }

      const signed = resolveSignedNotional({
        estimatedFillPrice: legPreview.preview.estimated_fill_price,
        side: leg.side,
        quantity: leg.quantity,
        orderType: optionOrderType,
        tick: leg.tick,
      });

      if (signed == null) return {
        combinedPremium: formatSignedPremium(null),
        warnings,
        riskReasons,
      };

      combinedSignedNotional += signed;
    }

    return {
      combinedPremium: formatSignedPremium(combinedSignedNotional),
      warnings,
      riskReasons,
    };
  }, [optionPreviewLegs, optionLegsResolved, optionPreviewMap, optionOrderType]);

  const buildStockRequest = (): PlaceOrderRequest => {
    const payload: PlaceOrderRequest = {
      client_order_id: buildClientOrderId(),
      symbol: stockSymbol,
      side: stockSide,
      quantity: stockQuantity,
      order_type: orderType,
    };

    if (orderType === 'limit') {
      payload.limit_price = Number(limitPrice);
    }

    return payload;
  };

  const buildOptionRequests = (): PlaceOrderRequest[] => {
    return optionLegsResolved.map((leg) => ({
      client_order_id: buildClientOrderId(),
      symbol: leg.symbol,
      side: leg.side,
      quantity: leg.quantity,
      order_type: optionOrderType,
    }));
  };

  const validate = (): string | null => {
    if (!upperUnderlying) return 'Complete instrument selection first';

    if (instrumentType === 'stock') {
      if (!stockSymbol) return 'Complete instrument selection first';
      if (!Number.isFinite(stockQuantity) || stockQuantity <= 0) return 'Quantity must be > 0';
      if (orderType === 'limit' && (!Number.isFinite(limitPrice) || limitPrice <= 0)) {
        return 'Limit price must be > 0';
      }
      return null;
    }

    if (optionLegsResolved.length < MIN_OPTION_LEGS) {
      return 'Add at least one option leg';
    }

    if (optionLegsResolved.length > MAX_OPTION_LEGS) {
      return `Option ticket supports at most ${MAX_OPTION_LEGS} legs`;
    }

    if (orderType === 'limit') {
      return 'Option multi-leg supports Market and Best Fill only';
    }

    for (let index = 0; index < optionLegsResolved.length; index += 1) {
      const leg = optionLegsResolved[index];
      if (!leg.expiration || leg.strike === '') {
        return `Complete Leg ${index + 1} first`;
      }
      if (!Number.isFinite(leg.quantity) || leg.quantity <= 0) {
        return `Leg ${index + 1} quantity must be > 0`;
      }
      if (!leg.symbol) {
        return `Complete Leg ${index + 1} first`;
      }
    }

    return null;
  };

  const clonePreview = (preview: OrderPreview): OrderPreview => ({
    ...preview,
    warnings: [...preview.warnings],
    risk: {
      ...preview.risk,
      reasons: [...preview.risk.reasons],
    },
  });

  const handlePreview = async () => {
    setError(null);
    const validationError = validate();

    if (validationError) {
      clearPreviewState();
      setError(validationError);
      return;
    }

    try {
      setPreviewing(true);

      if (instrumentType === 'stock') {
        const request = buildStockRequest();
        const result = await onPreview(request);
        setStockPreview(result);
        setOptionPreviewLegs(null);
        return;
      }

      const requests = buildOptionRequests();
      const settled = await Promise.allSettled(requests.map((request) => onPreview(request)));

      const nextLegPreviews: OptionLegPreviewResult[] = [];

      for (let index = 0; index < settled.length; index += 1) {
        const result = settled[index];
        const leg = optionLegsResolved[index];
        if (result.status === 'fulfilled') {
          nextLegPreviews.push({
            legId: leg.id,
            request: requests[index],
            preview: result.value,
          });
          continue;
        }

        const reason = result.reason as any;
        const msg = reason?.response?.data?.error || reason?.message || 'Preview failed';
        setOptionPreviewLegs(null);
        setStockPreview(null);
        setError(`Preview failed for leg ${index + 1} (${leg.symbol || 'incomplete'}): ${msg}`);
        return;
      }

      setOptionPreviewLegs(nextLegPreviews);
      setStockPreview(null);
    } catch (err: any) {
      clearPreviewState();
      setError(err?.response?.data?.error || err?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const closeConfirmModal = () => {
    if (submitting) return;
    setIsConfirmOpen(false);
    setConfirmSnapshot(null);
  };

  const closeSuccessModal = useCallback(() => {
    setIsSuccessOpen(false);
    setSuccessMessage(null);
  }, []);

  const closeMarketHoursBlockedModal = useCallback(() => {
    setIsMarketHoursBlockedModalOpen(false);
  }, []);

  const openMarketHoursBlockedModal = useCallback(() => {
    setIsConfirmOpen(false);
    setConfirmSnapshot(null);
    setError(null);
    setIsMarketHoursBlockedModalOpen(true);
  }, []);

  const resolveMarketHoursCheckSymbol = (snapshot: ConfirmSnapshot): string => {
    if (snapshot.mode === 'stock') {
      return snapshot.request.symbol;
    }
    return snapshot.legs[0]?.request.symbol ?? '';
  };

  const fetchMarketOpenWithRetry = useCallback(async (symbol: string): Promise<boolean | null> => {
    if (!onFetchMarketOpen) return null;

    try {
      return await onFetchMarketOpen(symbol);
    } catch {
      try {
        return await onFetchMarketOpen(symbol);
      } catch {
        return null;
      }
    }
  }, [onFetchMarketOpen]);

  const openSuccessModal = useCallback((message: string) => {
    setIsConfirmOpen(false);
    setConfirmSnapshot(null);
    setSuccessMessage(message);
    setIsSuccessOpen(true);
    if (onOrderPlaced) {
      setTimeout(onOrderPlaced, 700);
    }
  }, [onOrderPlaced]);

  const openConfirmModal = () => {
    closeSuccessModal();
    closeMarketHoursBlockedModal();
    setError(null);
    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    if (instrumentType === 'stock') {
      setConfirmSnapshot({
        mode: 'stock',
        request: buildStockRequest(),
        preview: stockPreview ? clonePreview(stockPreview) : null,
      });
      setIsConfirmOpen(true);
      return;
    }

    const requests = buildOptionRequests();
    const legs: OptionConfirmLeg[] = optionLegsResolved.map((leg, index) => ({
      leg,
      request: requests[index],
      preview: optionPreviewMap.get(leg.id)?.preview ? clonePreview(optionPreviewMap.get(leg.id)!.preview) : null,
    }));

    setConfirmSnapshot({
      mode: 'option',
      orderType: optionOrderType,
      legs,
    });
    setIsConfirmOpen(true);
  };

  const confirmPlaceOrder = async () => {
    if (!confirmSnapshot) return;

    const marketHoursCheckSymbol = resolveMarketHoursCheckSymbol(confirmSnapshot);
    if (
      isSimulationEnabled === false &&
      marketHoursCheckSymbol &&
      !isCryptoPairSymbol(marketHoursCheckSymbol)
    ) {
      const marketIsOpen = await fetchMarketOpenWithRetry(marketHoursCheckSymbol);
      if (marketIsOpen === false) {
        openMarketHoursBlockedModal();
        return;
      }
    }

    try {
      setSubmitting(true);

      if (confirmSnapshot.mode === 'stock') {
        await onPlaceOrder(confirmSnapshot.request);
        setStockPreview(null);
        setOptionPreviewLegs(null);
        setError(null);
        openSuccessModal('Order placed successfully.');
        return;
      }

      let placedCount = 0;
      for (let index = 0; index < confirmSnapshot.legs.length; index += 1) {
        const legOrder = confirmSnapshot.legs[index];
        try {
          await onPlaceOrder(legOrder.request);
          placedCount += 1;
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.message || 'Order placement failed';
          setIsConfirmOpen(false);
          setConfirmSnapshot(null);
          setError(
            `Placed ${placedCount}/${confirmSnapshot.legs.length} legs. Failed leg ${index + 1} (${legOrder.request.symbol}): ${msg}`,
          );
          return;
        }
      }

      setStockPreview(null);
      setOptionPreviewLegs(null);
      setError(null);
      openSuccessModal(`All ${confirmSnapshot.legs.length} option legs were placed successfully.`);
    } finally {
      setSubmitting(false);
    }
  };

  const submitValidationError = validate();
  const isSubmitDisabled = disabled || submitting || Boolean(submitValidationError);

  const displayOrderType = (value: OrderType) =>
    value
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const displaySide = (value: OrderSide) => value.charAt(0).toUpperCase() + value.slice(1);

  const confirmStockPremium = useMemo(() => {
    if (!confirmSnapshot || confirmSnapshot.mode !== 'stock' || !confirmSnapshot.preview) return null;
    const tick = ticksBySymbol[confirmSnapshot.request.symbol];
    const signedNotional = resolveSignedNotional({
      estimatedFillPrice: confirmSnapshot.preview.estimated_fill_price,
      side: confirmSnapshot.request.side,
      quantity: confirmSnapshot.request.quantity,
      orderType: confirmSnapshot.request.order_type,
      tick,
    });
    return formatSignedPremium(signedNotional);
  }, [confirmSnapshot, ticksBySymbol]);

  const confirmOptionCombinedPremium = useMemo(() => {
    if (!confirmSnapshot || confirmSnapshot.mode !== 'option') return null;

    let combined = 0;

    for (const legEntry of confirmSnapshot.legs) {
      const tick = ticksBySymbol[legEntry.request.symbol];
      const signedNotional = resolveSignedNotional({
        estimatedFillPrice: legEntry.preview?.estimated_fill_price ?? null,
        side: legEntry.request.side,
        quantity: legEntry.request.quantity,
        orderType: legEntry.request.order_type,
        tick,
      });

      if (signedNotional == null) {
        return formatSignedPremium(null);
      }

      combined += signedNotional;
    }

    return formatSignedPremium(combined);
  }, [confirmSnapshot, ticksBySymbol]);

  useEffect(() => {
    if (!isConfirmOpen && !isSuccessOpen && !isMarketHoursBlockedModalOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();

      if (isConfirmOpen) {
        if (!submitting) {
          setIsConfirmOpen(false);
          setConfirmSnapshot(null);
        }
        return;
      }

      if (isSuccessOpen) {
        closeSuccessModal();
        return;
      }

      if (isMarketHoursBlockedModalOpen) {
        closeMarketHoursBlockedModal();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [
    isConfirmOpen,
    isSuccessOpen,
    isMarketHoursBlockedModalOpen,
    submitting,
    closeSuccessModal,
    closeMarketHoursBlockedModal,
  ]);

  useEffect(() => {
    if (!isSuccessOpen) return;
    const timerId = window.setTimeout(() => {
      closeSuccessModal();
    }, 5000);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [isSuccessOpen, closeSuccessModal]);

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>Order Ticket</header>

      <div className={styles.instrumentTabs}>
        <button
          type="button"
          className={`${styles.instrumentTab} ${instrumentType === 'option' ? styles.instrumentTabActive : ''}`.trim()}
          aria-pressed={instrumentType === 'option'}
          data-testid="instrument-option-tab"
          onClick={() => {
            setInstrumentType('option');
            setError(null);
            clearPreviewState();
          }}
          disabled={disabled}
        >
          Option
        </button>
        <button
          type="button"
          className={`${styles.instrumentTab} ${instrumentType === 'stock' ? styles.instrumentTabActive : ''}`.trim()}
          aria-pressed={instrumentType === 'stock'}
          data-testid="instrument-stock-tab"
          onClick={() => {
            setInstrumentType('stock');
            setError(null);
            clearPreviewState();
          }}
          disabled={disabled}
        >
          Stock
        </button>
      </div>

      <div className={styles.grid2}>
        <label className={styles.fieldLabel}>Symbol <InfoTooltip text="The stock or ETF you want to trade. For options, this is the underlying symbol." /></label>
        <TickerDropdown
          value={underlying}
          onChange={(nextSymbol) => {
            setUnderlying(nextSymbol);
            optionContractsPreferredModeByLegRef.current = {};
            setOptionLegs((prev) => prev.map((leg) => ({
              ...leg,
              expiration: '',
              strike: '',
            })));
            setError(null);
            clearPreviewState();
          }}
          onCommit={(nextSymbol) => {
            setUnderlying(nextSymbol);
            optionContractsPreferredModeByLegRef.current = {};
            setOptionLegs((prev) => prev.map((leg) => ({
              ...leg,
              expiration: '',
              strike: '',
            })));
            setError(null);
            clearPreviewState();
          }}
          placeholder="Select symbol"
          disabled={disabled}
          allowClear={false}
          testId="underlying-input"
        />
      </div>

      {instrumentType === 'stock' && (
        <>
          {stockSymbol && (
            <div className={styles.tickerResolvedRow}>
              <span className={styles.fieldLabel}>Resolved:</span>
              <span className={styles.tickerResolved}>{stockSymbol}</span>
            </div>
          )}

          {stockSymbol && (
            <div className={styles.tickerQuoteBar}>
              <div className={styles.tickerQuoteItem}>
                <span className={styles.tickerQuoteLabel}>Bid</span>
                <span className={styles.tickerQuoteValue}>{formatQuote(stockQuoteTick?.bid)}</span>
              </div>
              <div className={styles.tickerQuoteItem}>
                <span className={styles.tickerQuoteLabel}>Ask</span>
                <span className={styles.tickerQuoteValue}>{formatQuote(stockQuoteTick?.ask)}</span>
              </div>
              <div className={styles.tickerQuoteItem}>
                <span className={styles.tickerQuoteLabel}>Last</span>
                <span className={styles.tickerQuoteValue}>{formatQuote(stockQuoteTick?.last)}</span>
              </div>
              <div className={styles.tickerQuoteItem}>
                <span className={styles.tickerQuoteLabel}>Time</span>
                <span className={styles.tickerQuoteTs}>
                  {stockQuoteTick?.ts ? new Date(stockQuoteTick.ts).toLocaleTimeString() : '--'}
                </span>
              </div>
            </div>
          )}

          <div className={styles.grid2} style={{ marginTop: 8 }}>
            <label className={styles.fieldLabel}>Side <InfoTooltip text="Buy to open a long position, Sell to close or short." /></label>
            <select
              className={styles.select}
              value={stockSide}
              onChange={(e) => {
                setStockSide(e.target.value as OrderSide);
                clearPreviewState();
              }}
              disabled={disabled}
              data-testid="stock-side-select"
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>

            <label className={styles.fieldLabel}>Quantity <InfoTooltip text="Number of shares to trade." /></label>
            <input
              className={styles.input}
              type="number"
              value={stockQuantity}
              onChange={(e) => {
                setStockQuantity(Number(e.target.value));
                clearPreviewState();
              }}
              min={1}
              disabled={disabled}
              data-testid="stock-quantity-input"
            />
          </div>
        </>
      )}

      {instrumentType === 'option' && (
        <>
          <div className={styles.optionLegHeaderRow}>
            <div className={styles.fieldLabel}>Option Legs <InfoTooltip text="Each leg is a separate option contract. Add multiple legs to build spreads, straddles, or other multi-leg strategies." /></div>
            <button
              type="button"
              className={styles.buttonSecondary}
              onClick={addOptionLeg}
              disabled={disabled || optionLegs.length >= MAX_OPTION_LEGS}
              data-testid="option-add-leg-button"
            >
              Add Leg
            </button>
          </div>

          <div className={styles.optionLegStack}>
            {optionLegsResolved.map((leg, index) => {
              const availableExpirations = availableExpirationsByLeg[leg.id] ?? [];
              const availableStrikes = availableStrikesByLeg[leg.id] ?? [];
              const contractsLoading = optionContractsLoadingByLeg[leg.id] === true;
              const contractsError = optionContractsErrorByLeg[leg.id];

              return (
                <div key={leg.id} className={styles.optionLegCard}>
                <div className={styles.optionLegCardHeader}>
                  <span className={styles.optionLegTitle}>Leg {index + 1}</span>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => removeOptionLeg(leg.id)}
                    disabled={disabled || optionLegsResolved.length <= MIN_OPTION_LEGS}
                    data-testid={`option-leg-${index}-remove-button`}
                  >
                    Remove
                  </button>
                </div>

                <div className={styles.optionLegGrid}>
                  <label className={styles.fieldLabel}>Side</label>
                  <select
                    className={styles.select}
                    value={leg.side}
                    onChange={(e) =>
                      updateOptionLeg(leg.id, (current) => ({
                        ...current,
                        side: e.target.value as OrderSide,
                      }))
                    }
                    disabled={disabled}
                    data-testid={`option-leg-${index}-side-select`}
                  >
                    <option value="buy">Buy</option>
                    <option value="sell">Sell</option>
                  </select>

                  <label className={styles.fieldLabel}>Type</label>
                  <select
                    className={styles.select}
                    value={leg.optionSide}
                    onChange={(e) => {
                      optionContractsPreferredModeByLegRef.current[leg.id] = null;
                      updateOptionLeg(leg.id, (current) => ({
                        ...current,
                        optionSide: e.target.value as OptionSide,
                        expiration: '',
                        strike: '',
                      }));
                    }}
                    disabled={disabled}
                    data-testid={`option-leg-${index}-type-select`}
                  >
                    <option value="call">Call</option>
                    <option value="put">Put</option>
                  </select>

                  <label className={styles.fieldLabel}>Expiration</label>
                  <select
                    className={styles.select}
                    value={leg.expiration}
                    onChange={(e) => {
                      optionContractsPreferredModeByLegRef.current[leg.id] = 'expiration';
                      updateOptionLeg(leg.id, (current) => ({
                        ...current,
                        expiration: e.target.value,
                      }));
                    }}
                    disabled={disabled || contractsLoading}
                    data-testid={`option-leg-${index}-expiration-select`}
                  >
                    <option value="">- select -</option>
                    {availableExpirations.map((exp) => (
                      <option key={`${leg.id}-${exp}`} value={exp}>{exp}</option>
                    ))}
                  </select>

                  <label className={styles.fieldLabel}>Strike</label>
                  <select
                    className={styles.select}
                    value={leg.strike}
                    onChange={(e) => {
                      optionContractsPreferredModeByLegRef.current[leg.id] = 'strike';
                      updateOptionLeg(leg.id, (current) => ({
                        ...current,
                        strike: e.target.value === '' ? '' : Number(e.target.value),
                      }));
                    }}
                    disabled={disabled || contractsLoading}
                    data-testid={`option-leg-${index}-strike-select`}
                  >
                    <option value="">- select -</option>
                    {availableStrikes.map((strikeValue) => (
                      <option key={`${leg.id}-${strikeValue}`} value={strikeValue}>{strikeValue.toFixed(2)}</option>
                    ))}
                  </select>

                  <label className={styles.fieldLabel}>Quantity</label>
                  <input
                    className={styles.input}
                    type="number"
                    min={1}
                    value={leg.quantity}
                    onChange={(e) =>
                      updateOptionLeg(leg.id, (current) => ({
                        ...current,
                        quantity: Number(e.target.value),
                      }))
                    }
                    disabled={disabled}
                    data-testid={`option-leg-${index}-quantity-input`}
                  />
                </div>

                <div className={styles.optionLegResolvedRow}>
                  <span className={styles.fieldLabel}>Resolved:</span>
                  <span className={styles.tickerResolved} data-testid={`option-leg-${index}-resolved-symbol`}>
                    {leg.symbol ? formatOptionResolvedSymbol(leg.symbol) : '--'}
                  </span>
                </div>
                {contractsLoading && (
                  <div className={styles.tickerQuoteTs}>Loading contracts...</div>
                )}
                {contractsError && (
                  <div className={styles.error}>{contractsError}</div>
                )}
              </div>
              );
            })}
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table} data-testid="option-leg-quote-table">
              <thead>
                <tr>
                  <th>Leg</th>
                  <th>Symbol</th>
                  <th>Bid</th>
                  <th>Ask</th>
                  <th>Last</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {optionLegsResolved.map((leg, index) => (
                  <tr key={`quote-${leg.id}`}>
                    <td>{index + 1}</td>
                    <td className={styles.tickerResolved}>{leg.symbol ? formatOptionResolvedSymbol(leg.symbol) : '--'}</td>
                    <td>{formatQuote(leg.tick?.bid)}</td>
                    <td>{formatQuote(leg.tick?.ask)}</td>
                    <td>{formatQuote(leg.tick?.last)}</td>
                    <td>{leg.tick?.ts ? new Date(leg.tick.ts).toLocaleTimeString() : '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.optionCombinedQuoteRow}>
            <div className={styles.optionCombinedQuoteItem}>
              <span className={styles.tickerQuoteLabel}>Combined Bid</span>
              <span
                className={`${styles.tickerQuoteValue} ${premiumClassName(optionCombinedQuote?.bidPremium.direction ?? null)}`.trim()}
                data-testid="option-combined-bid"
              >
                {optionCombinedQuote?.bidPremium.premiumText ?? '--'}
              </span>
            </div>
            <div className={styles.optionCombinedQuoteItem}>
              <span className={styles.tickerQuoteLabel}>Combined Ask</span>
              <span
                className={`${styles.tickerQuoteValue} ${premiumClassName(optionCombinedQuote?.askPremium.direction ?? null)}`.trim()}
                data-testid="option-combined-ask"
              >
                {optionCombinedQuote?.askPremium.premiumText ?? '--'}
              </span>
            </div>
            <div className={styles.optionCombinedQuoteItem}>
              <span className={styles.tickerQuoteLabel}>Combined Mid</span>
              <span
                className={`${styles.tickerQuoteValue} ${premiumClassName(optionCombinedQuote?.midPremium.direction ?? null)}`.trim()}
                data-testid="option-combined-mid"
              >
                {optionCombinedQuote?.midPremium.premiumText ?? '--'}
              </span>
            </div>
          </div>
        </>
      )}

      <div className={styles.grid2} style={{ marginTop: 8 }}>
        <label className={styles.fieldLabel}>Order Type <InfoTooltip text="Market fills immediately at current price. Limit lets you set a max/min price. Best Fill uses the mid-point between bid and ask." /></label>
        <select
          className={styles.select}
          value={orderType}
          onChange={(e) => {
            setOrderType(e.target.value as OrderType);
            clearPreviewState();
          }}
          disabled={disabled}
          data-testid="order-type-select"
        >
          <option value="market">Market</option>
          {instrumentType === 'stock' && <option value="limit">Limit</option>}
          <option value="best_fill">Best Fill</option>
        </select>

        {instrumentType === 'stock' && orderType === 'limit' && (
          <>
            <label className={styles.fieldLabel}>Limit Price</label>
            <input
              className={styles.input}
              type="number"
              step="0.01"
              value={limitPrice}
              onChange={(e) => {
                setLimitPrice(Number(e.target.value));
                clearPreviewState();
              }}
              disabled={disabled}
              data-testid="stock-limit-price-input"
            />
          </>
        )}
      </div>

      <div className={styles.actions}>
        <button className={styles.buttonSecondary} onClick={handlePreview} disabled={disabled || previewing || submitting}>
          {previewing ? 'Previewing...' : 'Preview'}
        </button>
        <button
          className={styles.buttonPrimary}
          onClick={openConfirmModal}
          disabled={isSubmitDisabled}
        >
          {submitting ? 'Submitting...' : 'Confirm & Place'}
        </button>
        <button
          type="button"
          className={styles.buttonSecondary}
          onClick={resetPersistedState}
          disabled={!manualStorageKey}
          title="Reset"
        >
          Reset
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {instrumentType === 'stock' && stockPreview && (
        <div className={styles.previewBox}>
          <div><strong>Accepted:</strong> {stockPreview.accepted ? 'Yes' : 'No'}</div>
          <div><strong>Estimated Fill:</strong> {stockPreview.estimated_fill_price?.toFixed(2) ?? '--'}</div>
          <div>
            <strong>Estimated Premium:</strong>{' '}
            <span className={premiumClassName(stockPreviewPremium?.direction ?? null)}>
              {stockPreviewPremium?.premiumText ?? '--'}
            </span>
          </div>
          {stockPreview.warnings.length > 0 && (
            <div className={styles.warningList}>Warnings: {stockPreview.warnings.join(' | ')}</div>
          )}
          {!stockPreview.risk.passed && (
            <div className={styles.errorList}>Risk Reject: {stockPreview.risk.reasons.join(' | ')}</div>
          )}
        </div>
      )}

      {instrumentType === 'option' && optionPreviewLegs && (
        <div className={styles.previewBox} data-testid="option-preview-box">
          <div><strong>Leg Previews:</strong> {optionPreviewLegs.length}</div>

          {optionLegsResolved.map((leg, index) => {
            const legPreview = optionPreviewMap.get(leg.id);
            const signedNotional = resolveSignedNotional({
              estimatedFillPrice: legPreview?.preview.estimated_fill_price ?? null,
              side: leg.side,
              quantity: leg.quantity,
              orderType: optionOrderType,
              tick: leg.tick,
            });
            const premium = formatSignedPremium(signedNotional);

            return (
              <div key={`preview-${leg.id}`} className={styles.previewLegRow}>
                <div><strong>Leg {index + 1}:</strong> {leg.symbol ? formatOptionResolvedSymbol(leg.symbol) : '--'}</div>
                <div><strong>Est. Fill:</strong> {legPreview?.preview.estimated_fill_price?.toFixed(2) ?? '--'}</div>
                <div>
                  <strong>Est. Premium:</strong>{' '}
                  <span className={premiumClassName(premium.direction)}>{premium.premiumText}</span>
                </div>
              </div>
            );
          })}

          <div>
            <strong>Combined Estimated Premium:</strong>{' '}
            <span className={premiumClassName(optionPreviewMeta?.combinedPremium.direction ?? null)} data-testid="option-combined-preview-premium">
              {optionPreviewMeta?.combinedPremium.premiumText ?? '--'}
            </span>
          </div>

          {optionPreviewMeta && optionPreviewMeta.warnings.length > 0 && (
            <div className={styles.warningList}>Warnings: {optionPreviewMeta.warnings.join(' | ')}</div>
          )}
          {optionPreviewMeta && optionPreviewMeta.riskReasons.length > 0 && (
            <div className={styles.errorList}>Risk Reject: {optionPreviewMeta.riskReasons.join(' | ')}</div>
          )}
        </div>
      )}

      {isConfirmOpen && confirmSnapshot && (
        <div
          className={styles.confirmBackdrop}
          onClick={(event) => {
            if (event.target === event.currentTarget) closeConfirmModal();
          }}
        >
          <div className={styles.confirmModal} role="dialog" aria-modal="true" aria-labelledby="order-confirm-title">
            <div className={styles.confirmHeader}>
              <h3 id="order-confirm-title" className={styles.confirmTitle}>Confirm Order</h3>
            </div>

            <div className={styles.confirmBody}>
              {confirmSnapshot.mode === 'stock' && (
                <>
                  <p className={styles.confirmDescription}>
                    Please confirm you want to place this stock order.
                  </p>

                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Symbol</span>
                    <span className={styles.confirmValue}>{confirmSnapshot.request.symbol}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Side</span>
                    <span className={styles.confirmValue}>{displaySide(confirmSnapshot.request.side)}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Quantity</span>
                    <span className={styles.confirmValue}>{confirmSnapshot.request.quantity}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Order Type</span>
                    <span className={styles.confirmValue}>{displayOrderType(confirmSnapshot.request.order_type)}</span>
                  </div>

                  {confirmSnapshot.request.order_type === 'limit' && typeof confirmSnapshot.request.limit_price === 'number' && (
                    <div className={styles.confirmRow}>
                      <span className={styles.confirmLabel}>Limit Price</span>
                      <span className={styles.confirmValue}>{confirmSnapshot.request.limit_price.toFixed(2)}</span>
                    </div>
                  )}

                  {confirmSnapshot.preview && (
                    <>
                      <div className={styles.confirmRow}>
                        <span className={styles.confirmLabel}>Est. Fill</span>
                        <span className={styles.confirmValue}>
                          {confirmSnapshot.preview.estimated_fill_price != null
                            ? confirmSnapshot.preview.estimated_fill_price.toFixed(2)
                            : '--'}
                        </span>
                      </div>
                      <div className={styles.confirmRow}>
                        <span className={styles.confirmLabel}>Est. Premium</span>
                        <span className={`${styles.confirmValue} ${premiumClassName(confirmStockPremium?.direction ?? null)}`.trim()}>
                          {confirmStockPremium?.premiumText ?? '--'}
                        </span>
                      </div>
                    </>
                  )}
                </>
              )}

              {confirmSnapshot.mode === 'option' && (
                <>
                  <p className={styles.confirmDescription}>
                    This will place {confirmSnapshot.legs.length} option legs sequentially as linked orders.
                  </p>

                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Order Type</span>
                    <span className={styles.confirmValue}>{displayOrderType(confirmSnapshot.orderType)}</span>
                  </div>

                  {confirmSnapshot.legs.map((entry, index) => {
                    const signedNotional = resolveSignedNotional({
                      estimatedFillPrice: entry.preview?.estimated_fill_price ?? null,
                      side: entry.request.side,
                      quantity: entry.request.quantity,
                      orderType: entry.request.order_type,
                      tick: ticksBySymbol[entry.request.symbol],
                    });
                    const premium = formatSignedPremium(signedNotional);

                    return (
                      <div key={`confirm-leg-${entry.leg.id}`} className={styles.confirmLegBlock}>
                        <div className={styles.confirmLegTitle}>Leg {index + 1}</div>
                        <div className={styles.confirmRow}>
                          <span className={styles.confirmLabel}>Symbol</span>
                          <span className={styles.confirmValue}>{formatOptionResolvedSymbol(entry.request.symbol)}</span>
                        </div>
                        <div className={styles.confirmRow}>
                          <span className={styles.confirmLabel}>Side / Qty</span>
                          <span className={styles.confirmValue}>{displaySide(entry.request.side)} {entry.request.quantity}</span>
                        </div>
                        <div className={styles.confirmRow}>
                          <span className={styles.confirmLabel}>Est. Fill</span>
                          <span className={styles.confirmValue}>{entry.preview?.estimated_fill_price?.toFixed(2) ?? '--'}</span>
                        </div>
                        <div className={styles.confirmRow}>
                          <span className={styles.confirmLabel}>Est. Premium</span>
                          <span className={`${styles.confirmValue} ${premiumClassName(premium.direction)}`.trim()}>{premium.premiumText}</span>
                        </div>
                      </div>
                    );
                  })}

                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Est. Combined Premium</span>
                    <span className={`${styles.confirmValue} ${premiumClassName(confirmOptionCombinedPremium?.direction ?? null)}`.trim()}>
                      {confirmOptionCombinedPremium?.premiumText ?? '--'}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmButtonNo}
                onClick={closeConfirmModal}
                disabled={submitting}
              >
                No
              </button>
              <button
                type="button"
                className={styles.confirmButtonYes}
                onClick={confirmPlaceOrder}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSuccessOpen && successMessage && (
        <div
          className={styles.confirmBackdrop}
          onClick={(event) => {
            if (event.target === event.currentTarget) closeSuccessModal();
          }}
          data-testid="order-success-modal"
        >
          <div className={styles.confirmModal} role="dialog" aria-modal="true" aria-labelledby="order-success-title">
            <div className={styles.confirmHeader}>
              <h3 id="order-success-title" className={`${styles.confirmTitle} ${styles.successTitle}`.trim()}>
                Order Placed
              </h3>
            </div>

            <div className={styles.confirmBody}>
              <p className={`${styles.confirmDescription} ${styles.successDescription}`.trim()}>{successMessage}</p>
            </div>

          </div>
        </div>
      )}

      {isMarketHoursBlockedModalOpen && (
        <div
          className={styles.confirmBackdrop}
          onClick={(event) => {
            if (event.target === event.currentTarget) closeMarketHoursBlockedModal();
          }}
          data-testid="order-market-hours-modal"
        >
          <div className={styles.confirmModal} role="dialog" aria-modal="true" aria-labelledby="order-market-hours-title">
            <div className={styles.confirmHeader}>
              <h3 id="order-market-hours-title" className={styles.confirmTitle}>
                Order Not Allowed
              </h3>
            </div>

            <div className={styles.confirmBody}>
              <p className={styles.confirmDescription}>{MARKET_HOURS_BLOCKED_MESSAGE}</p>
            </div>

            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmButtonYes}
                onClick={closeMarketHoursBlockedModal}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
